import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { ErrorMessage, HelpText, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import type { Network } from '#dn-types/network.ts';
import { getCidrV4, getCidrV6 } from '#util/networkCidrs.ts';
import styles from './DualStackAddressFields.module.css';

type DualStackFields = { ipv4Address: string; ipv6Address: string; networkID: string; ipv4Enabled: boolean };

type Props = {
  /** Networks available to the org; used to determine IP input shape based on selected network CIDRs */
  networks: readonly Network[];
  /** Disables both IP inputs (e.g. when editing an existing host or no network is selected). */
  disabled?: boolean;
};

/**
 * Renders the IPv4/IPv6 address inputs based on the selected network's CIDR shape:
 *   - v4 only: single "IP address" input bound to ipv4Address
 *   - v6 only: single "IP address" input bound to ipv6Address
 *   - dual stack: side-by-side IPv6 + IPv4 inputs; IPv4 defaults to auto-assign (blank)
 *     with an inline "Disable IPv4" / "Enable IPv4" toggle link to opt out of v4 entirely
 *
 * Owns the cross-family clearing effect: when the selected network drops a family,
 * the corresponding form value (and any error) is cleared so a stale value can't
 * slip into the submit payload or block submission via a hidden input's validation.
 */
export function DualStackAddressFields({ networks, disabled = false }: Props) {
  const { register, formState, control, setValue, clearErrors } = useFormContext<DualStackFields>();
  const { errors } = formState;

  const [networkID, ipv4Enabled] = useWatch({ control, name: ['networkID', 'ipv4Enabled'] });
  const selectedNetwork = networks.find((n) => n.id === networkID);
  const hasV4 = selectedNetwork ? Boolean(getCidrV4(selectedNetwork)) : false;
  const hasV6 = selectedNetwork ? Boolean(getCidrV6(selectedNetwork)) : false;

  useEffect(() => {
    if (!hasV4) {
      setValue('ipv4Address', '', { shouldDirty: false });
      clearErrors('ipv4Address');
    }
    if (!hasV6) {
      setValue('ipv6Address', '', { shouldDirty: false });
      clearErrors('ipv6Address');
    }
    // When the network is no longer dual-stack, reset ipv4Enabled to the default (enabled)
    if (!hasV4 || !hasV6) {
      setValue('ipv4Enabled', true, { shouldDirty: false });
    }
  }, [hasV4, hasV6, setValue, clearErrors]);

  return (
    hasV4 && !hasV6 ?
      <Field label="IP address">
        <HelpText>Specify IPv4 address or leave blank for auto-assignment.</HelpText>
        <Input
          className="width-100"
          placeholder="Auto assigned"
          hasError={Boolean(errors.ipv4Address)}
          {...register('ipv4Address', { disabled })}
        />
        {errors.ipv4Address && <ErrorMessage>{errors.ipv4Address.message}</ErrorMessage>}
      </Field>
    : !hasV4 && hasV6 ?
      <Field label="IP address">
        <HelpText>Specify IPv6 address or leave blank for auto-assignment.</HelpText>
        <Input
          className="width-100"
          placeholder="Auto assigned"
          hasError={Boolean(errors.ipv6Address)}
          {...register('ipv6Address', { disabled })}
        />
        {errors.ipv6Address && <ErrorMessage>{errors.ipv6Address.message}</ErrorMessage>}
      </Field>
    : hasV4 && hasV6 ?
      <div className={styles.DualStackAddressFields___twoColumnGrid}>
        <Field label="IPv6 address" className={styles.DualStackAddressFields___column}>
          <HelpText>Specify IPv6 address or leave blank for auto-assignment.</HelpText>
          <Input
            className="width-100"
            placeholder="Auto assigned"
            hasError={Boolean(errors.ipv6Address)}
            {...register('ipv6Address', { disabled })}
          />
          {errors.ipv6Address && <ErrorMessage>{errors.ipv6Address.message}</ErrorMessage>}
        </Field>
        {/* On v2 dual-stack networks, blank IPv4 = auto-assign (send the network CIDR as the signal).
            Clicking "Disable IPv4" sets ipv4Enabled: false, which causes the submit handler to omit
            v4 from ipAddresses entirely — the API interprets that as no v4 assignment. */}
        <Field label="IPv4 address" className={styles.DualStackAddressFields___column}>
          <HelpText className="flex align-baseline justify-between gap-4">
            <span>
              {ipv4Enabled ?
                'Specify IPv4 address or leave blank for auto-assignment'
              : 'IPv4 is disabled for this host'}
            </span>
            {!disabled &&
              (ipv4Enabled ?
                <InlineButton
                  className="shrink-0 text-right"
                  onClick={() => {
                    setValue('ipv4Enabled', false, { shouldDirty: true });
                    setValue('ipv4Address', '');
                    clearErrors('ipv4Address');
                  }}
                >
                  Disable IPv4
                </InlineButton>
              : <InlineButton
                  className="shrink-0 text-right"
                  onClick={() => setValue('ipv4Enabled', true, { shouldDirty: true })}
                >
                  Enable IPv4
                </InlineButton>)}
          </HelpText>
          <Input
            className="width-100"
            placeholder={ipv4Enabled ? 'Auto assigned' : 'Not assigned'}
            hasError={Boolean(errors.ipv4Address)}
            {...register('ipv4Address', { disabled: disabled || !ipv4Enabled })}
          />
          {errors.ipv4Address && <ErrorMessage>{errors.ipv4Address.message}</ErrorMessage>}
        </Field>
      </div>
    : null
  );
}
